import { z } from 'zod';
import { LOCALES } from './i18n';
import { Birthday, Remind, ReminderSettings } from './reminders';
import { Aisle } from './aisles';
import { REGIONS } from './holidays';

/**
 * Domain model (Phase 1). Firestore layout: households/{householdId}/<collection>/{id}.
 *
 * Dates and times are stored the way families think about them: local calendar dates
 * ("2026-10-01") and wall-clock times ("16:00") in the household's time zone. Technical
 * timestamps (createdAt …) are epoch milliseconds.
 */

export const personColors = [
  'clay',
  'sun',
  'pine',
  'lagoon',
  'berry',
  'plum',
  'meadow',
  'stone',
] as const;
export const PersonColor = z.enum(personColors);
export type PersonColor = z.infer<typeof PersonColor>;

export const Role = z.enum(['parent', 'teen', 'kid']);
export type Role = z.infer<typeof Role>;

/** picture: reads little (≈4–7), kid: ≈8–12, teen: compact UI. Parents use 'teen' layout. */
export const AgeProfile = z.enum(['picture', 'kid', 'teen']);
export type AgeProfile = z.infer<typeof AgeProfile>;

const id = z.string().min(1).max(128);
export const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');
export const ClockTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM');
const millis = z.number().int().nonnegative();
const title = z.string().trim().min(1).max(80);
/** Name of a picture in the app's icon set (e.g. "tooth", "ball"). */
const iconKey = z.string().min(1).max(32);

export const Location = z.object({
  name: z.string().min(1).max(80),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  /** German federal state for school and public holidays (1.24), from the place search. */
  region: z.enum(REGIONS).optional(),
});
export type Location = z.infer<typeof Location>;

export const Household = z.object({
  id,
  name: z.string().trim().min(1).max(60),
  /** IANA time zone – all "today" logic uses it. */
  timeZone: z.string().default('Europe/Berlin'),
  /** City-level location for weather and daylight only (DSGVO data minimisation). */
  location: Location.optional(),
  /**
   * Familiensprache (1.19): reminders, mails, starter content and the KI answer in it. Each device
   * shows the app in its own language. Missing = German (families before 1.19 stay German).
   */
  locale: z.enum(LOCALES).optional(),
  /** Colours follow the sun on every device; missing = 'subtle' (the default). */
  daylight: z.enum(['off', 'subtle', 'strong']).optional(),
  /** Resting wall display ("Family Atmosphere"); missing = the defaults below. */
  atmosphere: z
    .object({
      enabled: z.boolean(),
      /** Minutes without a touch until the wall rests. */
      idleMinutes: z.number().int().min(1).max(30),
      /** Night rest (almost black) from … to …; null = no night rest. */
      sleepFrom: ClockTime.nullable(),
      sleepTo: ClockTime,
    })
    .optional(),
  /**
   * KI-Helfer ("Einfach sagen"): parents switch it on after reading what it does. Off when
   * missing – nothing goes to the AI before a parent agreed. The app's key is a server secret.
   */
  ai: z.object({ enabled: z.boolean() }).optional(),
  /**
   * Testfamilie (1.17): came in through a personal invitation – everything free during the test.
   * 'plus' (1.21): Plus switched on by an admin until payment exists. Written by the server only;
   * what a family has right now comes from `planOf` (plus.ts) – trial included.
   */
  plan: z.enum(['testfamily', 'plus', 'free']).optional(),
  planSince: z.number().optional(),
  /** Essensplan (Küche 3): the family's usual dinner time; missing = MEAL_TIME (18:30). */
  mealTime: ClockTime.optional(),
  /** Erinnerungen (1.13): family defaults; missing = REMINDER_DEFAULTS. */
  reminders: ReminderSettings.optional(),
  /** Festive days on "Heute" and the resting wall (1.13); missing = on, Halloween off. */
  festive: z.object({ enabled: z.boolean(), off: z.array(z.string().max(20)).max(10) }).optional(),
  createdAt: millis,
  /** Filled with the demo family (households before 2026-10-01 lack the flag). */
  demo: z.boolean().optional(),
});
export type Household = z.infer<typeof Household>;

export const Member = z.object({
  id,
  /** First name or nickname only – no surnames, no birth dates for kids. */
  name: z.string().trim().min(1).max(30),
  role: Role,
  ageProfile: AgeProfile,
  color: PersonColor,
  emoji: z.string().max(8).optional(),
  /** Firebase Auth uid for members with their own login (parents, teens). */
  uid: z.string().optional(),
  /** Denormalised point balance – only ever written by Cloud Functions together with a ledger entry. */
  balance: z.number().int().default(0),
  /** "MM-DD" – day and month only, no year (1.13: birthday view, reminders). */
  birthday: Birthday.optional(),
  /** Reward the member is saving for (star jar / progress ring). */
  goalRewardId: z.string().optional(),
  sortOrder: z.number().int().default(0),
  archived: z.boolean().default(false),
  /** Essensplan (Küche 3): allergies and dislikes – a warning in the plan, never a filter. */
  food: z
    .object({
      allergies: z.array(z.string().trim().min(1).max(30)).max(10).default([]),
      dislikes: z.array(z.string().trim().min(1).max(30)).max(10).default([]),
    })
    .optional(),
});
export type Member = z.infer<typeof Member>;

// ---------------------------------------------------------------------------------------------
// Shared vs personal
// ---------------------------------------------------------------------------------------------

/**
 * Who something belongs to – the same rule for tasks, to-dos, events, wishes …:
 *  - 'all'               everybody / "zu haben": whoever gets to it first
 *  - ['lotta']            one person
 *  - ['lotta', 'jonas']     shared between these people: whoever ticks it first
 */
export const Assignees = z.union([z.literal('all'), z.array(id).min(1).max(20)]);
export type Assignees = z.infer<typeof Assignees>;

export const isForEveryone = (a: Assignees) => a === 'all';
export const isShared = (a: Assignees) => a === 'all' || a.length > 1;
export const includesMember = (a: Assignees, memberId: string) =>
  a === 'all' || a.includes(memberId);

// ---------------------------------------------------------------------------------------------
// Tasks (with stars) and to-dos (without)
// ---------------------------------------------------------------------------------------------

export const Daypart = z.enum(['morning', 'afternoon', 'evening', 'anytime']);
export type Daypart = z.infer<typeof Daypart>;

/** Simple repetition – what families actually use. 1 = Monday … 7 = Sunday. */
export const Repeat = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('once'), date: IsoDate }),
  z.object({ kind: z.literal('daily') }),
  z.object({ kind: z.literal('weekly'), days: z.array(z.number().int().min(1).max(7)).min(1) }),
  /**
   * Once within a period, any day (Mike, 04.10.): "1× pro Woche" (Mon–Sun), "1× am Wochenende"
   * (Sat or Sun), "1× im Monat". Shown every day of the period until it is done.
   */
  z.object({ kind: z.literal('flex'), per: z.enum(['week', 'weekend', 'month']) }),
]);
export type Repeat = z.infer<typeof Repeat>;

export const TaskSource = z.enum(['manual', 'waste', 'template']);

export const Task = z.object({
  id,
  title,
  icon: iconKey,
  points: z.number().int().min(0).max(500),
  daypart: Daypart,
  assignees: Assignees,
  repeat: Repeat,
  /** Parents confirm before stars are booked. */
  requiresApproval: z.boolean(),
  active: z.boolean().default(true),
  source: TaskSource.default('manual'),
  /** e.g. the waste source + date that created "Gelbe Tonne rausstellen". */
  sourceRef: z.string().optional(),
  createdBy: z.string().optional(),
  createdAt: millis.optional(),
});
export type Task = z.infer<typeof Task>;

/**
 * State of a task on one day. Document id `${taskId}_${date}` makes "who was first" a natural
 * race: the first tick creates the document, everybody else sees it is taken.
 *  open → done      (no approval needed: stars at once)
 *  open → pending   (approval needed) → approved (stars) | open (sent back with a note)
 */
export const TaskInstanceStatus = z.enum(['open', 'pending', 'done', 'approved']);
export type TaskInstanceStatus = z.infer<typeof TaskInstanceStatus>;

export const TaskInstance = z.object({
  id,
  taskId: id,
  date: IsoDate,
  /** Who did it (for shared tasks: who was first). */
  memberId: id,
  status: TaskInstanceStatus,
  doneAt: millis.optional(),
  /** Parent's note when sent back ("Schau nochmal drüber"). */
  note: z.string().max(140).optional(),
  /** "Zusammen gemacht": the others who helped – each gets a share of the stars, rounded up. */
  with: z.array(id).max(8).optional(),
  /** Stars already booked for this instance – maintained by Functions only. */
  awarded: z.number().int().optional(),
  /** Since 1.9: stars booked per person (several when done together) – Functions only. */
  awardedMap: z.record(z.string(), z.number().int()).optional(),
});
export type TaskInstance = z.infer<typeof TaskInstance>;

export const Todo = z.object({
  id,
  title,
  icon: iconKey,
  assignees: Assignees,
  due: IsoDate.optional(),
  done: z.boolean().default(false),
  doneBy: z.string().optional(),
  doneAt: millis.optional(),
  createdBy: z.string().optional(),
  createdAt: millis.optional(),
});
export type Todo = z.infer<typeof Todo>;

// ---------------------------------------------------------------------------------------------
// Stars
// ---------------------------------------------------------------------------------------------

export const LedgerType = z.enum(['task', 'kudos', 'redeem', 'manual']);
export type LedgerType = z.infer<typeof LedgerType>;

/** Append-only points journal. Written exclusively by Cloud Functions. */
export const LedgerEntry = z.object({
  memberId: id,
  delta: z.number().int(),
  type: LedgerType,
  reason: z.string().max(140),
  refId: z.string().optional(),
  createdBy: z.string(),
  createdAt: millis,
});
export type LedgerEntry = z.infer<typeof LedgerEntry>;

export const Kudos = z.object({
  id,
  memberId: id,
  text: z.string().trim().min(1).max(140),
  points: z.number().int().min(0).max(100),
  createdBy: z.string(),
  createdAt: millis,
});
export type Kudos = z.infer<typeof Kudos>;

/**
 * A note on the family pinboard ("Oma kommt Sonntag") – everybody reads, parents and teens pin
 * and take down. With `until` it disappears by itself after that day.
 */
export const NOTE_MAX = 200;
export const Note = z.object({
  id,
  text: z.string().trim().min(1).max(NOTE_MAX),
  /** Member who pinned it; missing when pinned on the wall display with the parent PIN. */
  createdBy: z.string().optional(),
  until: IsoDate.optional(),
  createdAt: millis,
});
export type Note = z.infer<typeof Note>;

export const Reward = z.object({
  id,
  title: z.string().trim().min(1).max(60),
  icon: iconKey,
  cost: z.number().int().min(1).max(100000),
  /** Who may redeem it. Empty = all kids and teens. */
  memberIds: z.array(id).default([]),
  active: z.boolean().default(true),
});
export type Reward = z.infer<typeof Reward>;

export const Redemption = z.object({
  id,
  rewardId: id,
  memberId: id,
  cost: z.number().int().min(1),
  status: z.enum(['requested', 'approved', 'declined']),
  createdAt: millis,
  decidedBy: z.string().optional(),
});
export type Redemption = z.infer<typeof Redemption>;

/** The family goal (one document: goals/family). Progress grows with every star earned. */
export const Goal = z.object({
  title: z.string().trim().min(1).max(60),
  target: z.number().int().min(1),
  progress: z.number().int().min(0).default(0),
});
export type Goal = z.infer<typeof Goal>;

// ---------------------------------------------------------------------------------------------
// Calendar
// ---------------------------------------------------------------------------------------------

/** Where an event came from. `paper` is reserved for the A3 photo import, `ai` for Magic Add. */
export const EventSource = z.enum(['manual', 'ics', 'waste', 'ai', 'paper']);
export type EventSource = z.infer<typeof EventSource>;

export const CalendarEvent = z.object({
  id,
  title: z.string().trim().min(1).max(120),
  icon: iconKey.default('calendar'),
  date: IsoDate,
  /** Multi-day events (holidays, trips). */
  endDate: IsoDate.optional(),
  /**
   * Weekly series ("Fußball Mo + Mi"): `date` is the first day, `days` 1 = Mon … 7 = Sun.
   * `recurring` mirrors its presence so the app can load series older than its date window.
   */
  repeat: z
    .object({
      /** Weekly days; empty for monthly and yearly series (1.25). */
      days: z.array(z.number().int().min(1).max(7)).max(7),
      /** Kalender 2 (1.25): missing = weekly, as every series before. See `series.ts`. */
      freq: z.enum(['daily', 'weekly', 'monthly', 'yearly']).optional(),
      /** Every n-th day/week/month/year, counted from `date` (1 = every). */
      every: z.number().int().min(1).max(12).optional(),
      monthBy: z.enum(['date', 'weekday']).optional(),
      until: IsoDate.optional(),
      /** Ends after this many dates. */
      count: z.number().int().min(1).max(500).optional(),
      /** Single dates taken out of the series (deleted on their own). */
      skip: z.array(IsoDate).max(400).optional(),
      /** Pause in the school holidays of the family's state. */
      skipHolidays: z.boolean().optional(),
      /** "Nur diesen Termin" at another time. */
      moved: z
        .record(IsoDate, z.object({ start: ClockTime.optional(), end: ClockTime.optional() }))
        .optional(),
    })
    .optional(),
  recurring: z.boolean().optional(),
  /** Absent = all day. */
  start: ClockTime.optional(),
  end: ClockTime.optional(),
  /** 'all' = whole family, otherwise the people involved (one or several). */
  assignees: Assignees,
  location: z.string().max(120).optional(),
  /** Logistics: "Papa bringt · Mama holt". */
  transport: z.string().max(120).optional(),
  bring: z.array(z.string().max(40)).max(12).default([]),
  notes: z.string().max(1000).optional(),
  source: EventSource.default('manual'),
  sourceId: z.string().optional(),
  createdBy: z.string().optional(),
  /** "Erinnern" (1.13): missing = the family default. */
  remind: Remind.optional(),
  /** Stars for ticking the event off (1.25, Mike 20.10.) – set by parents only. */
  stars: z.number().int().min(0).max(20).optional(),
});
export type CalendarEvent = z.infer<typeof CalendarEvent>;

/**
 * An event ticked off on one day (1.25) – like a task instance: id `${eventId}_${date}`, stars are
 * booked by Functions only.
 */
export const EventCheck = z.object({
  id,
  eventId: id,
  date: IsoDate,
  memberId: id,
  status: z.enum(['open', 'done']),
  doneAt: millis.optional(),
  awardedMap: z.record(z.string(), z.number().int()).optional(),
});
export type EventCheck = z.infer<typeof EventCheck>;

/** A shift plan (1.25): one person, named shifts and a rhythm that repeats. */
export const ShiftType = z.object({
  name: z.string().trim().min(1).max(20),
  start: ClockTime,
  end: ClockTime,
});
export const ShiftPlan = z.object({
  id,
  memberId: id,
  /** F, S, N, T – the family names them ("Früh", "Spät" …). */
  types: z.partialRecord(z.enum(['F', 'S', 'N', 'T']), ShiftType),
  /** One key per day: a shift, "-" free. */
  pattern: z
    .array(z.enum(['F', 'S', 'N', 'T', '-']))
    .min(1)
    .max(28),
  start: IsoDate,
  until: IsoDate.optional(),
  /** Only on this day: another shift, "-" free or "U" holiday. */
  over: z.record(IsoDate, z.enum(['F', 'S', 'N', 'T', '-', 'U'])).optional(),
  createdBy: z.string().optional(),
});
export type ShiftPlan = z.infer<typeof ShiftPlan>;

/**
 * Subscribed calendars, read-only:
 *  - ics:   any calendar link – school, club, waste, and also Google ("geheime Adresse"),
 *           Outlook ("Kalender veröffentlichen") or iCloud ("öffentlicher Kalender")
 *  - waste: Abfuhrtermine straight from the waste company (Jumomind, AbfallNavi, AWIDO, abfall.io)
 * An ics link can be marked as the family's waste calendar (`bins`, W2) – any provider works.
 */
/** "… rausstellen" tasks the evening before: who takes the bins out, for how many stars. */
export const BinReminder = z.object({
  assignees: Assignees,
  points: z.number().int().min(0).max(100),
});
export type BinReminder = z.infer<typeof BinReminder>;

/** The waste companies' interfaces we can ask directly (1.15; before only Jumomind/Minden). */
export const WasteProvider = z.enum(['jumomind', 'abfallnavi', 'awido', 'abfallio']);
export type WasteProvider = z.infer<typeof WasteProvider>;

export const WasteConfig = z.object({
  /** Missing in connections from before 1.15 – they are all Jumomind (Minden). */
  provider: WasteProvider.default('jumomind'),
  /** Company at the provider (Jumomind service, AWIDO customer, abfall.io key …). */
  service: z.string().min(1).max(64),
  cityId: z.string().min(1).max(64),
  /** The address at the provider (house, street or whole town – provider-specific id). */
  areaId: z.string().min(1).max(200),
  /** Names as the family picked them: ids can change, names let the sync find the address again. */
  city: z.string().max(80).optional(),
  street: z.string().max(120),
  houseNumber: z.string().max(40).optional(),
  /** Bin types the family has (provider keys, e.g. "SBM_GELB", "Restmüll"). Empty = all. */
  trashTypes: z.array(z.string().max(80)).default([]),
  /** Create "… rausstellen" tasks the evening before. */
  reminder: BinReminder.optional(),
});
export type WasteConfig = z.infer<typeof WasteConfig>;

/** A calendar link that is the waste calendar of any provider (W2). */
export const BinsConfig = z.object({
  /** Bin labels the family has (see binLabel, e.g. "Restmüll"). Empty = all. */
  trashTypes: z.array(z.string().max(60)).max(20).default([]),
  reminder: BinReminder.optional(),
});
export type BinsConfig = z.infer<typeof BinsConfig>;

export const CalendarSource = z.object({
  id,
  kind: z.enum(['ics', 'waste']),
  title: z.string().trim().min(1).max(60),
  icon: iconKey.default('calendar'),
  /** ics only. Treated like a password: readable by parents only. */
  url: z.string().url().max(2000).optional(),
  waste: WasteConfig.optional(),
  /** ics only: this link is the waste calendar – events become bins, with reminders. */
  bins: BinsConfig.optional(),
  assignees: Assignees,
  lastSyncAt: millis.optional(),
  /** "Erinnern" for every event of this calendar (1.13); missing = the family default. */
  remind: Remind.optional(),
  lastError: z.string().max(300).optional(),
  createdBy: z.string().optional(),
});
export type CalendarSource = z.infer<typeof CalendarSource>;

/**
 * School / kindergarten week of one child (document id = member id). Generates the daily
 * school span in the shared calendar and the timetable view. Prepared for photo import.
 */
export const TimetableDay = z.object({
  start: ClockTime,
  end: ClockTime,
  lessons: z.array(z.string().max(40)).max(12).default([]),
});
export type TimetableDay = z.infer<typeof TimetableDay>;

export const Timetable = z.object({
  memberId: id,
  /** "Schule" or "Kita". */
  label: z.string().max(30).default('Schule'),
  /** Keys "1" (Monday) … "5" (Friday). */
  days: z.partialRecord(z.enum(['1', '2', '3', '4', '5']), TimetableDay),
});
export type Timetable = z.infer<typeof Timetable>;

// ---------------------------------------------------------------------------------------------
// Callable inputs (validated server-side)
// ---------------------------------------------------------------------------------------------

export const NewMember = Member.pick({
  name: true,
  role: true,
  ageProfile: true,
  color: true,
  emoji: true,
});
export type NewMember = z.infer<typeof NewMember>;

export const CreateHouseholdInput = z.object({
  name: z.string().trim().min(1).max(60),
  location: Location.nullish(),
  /** Familiensprache, taken from the creating device; older apps send none → German. */
  locale: z.enum(LOCALES).nullish(),
  /** The signed-in parent creating the household. */
  me: NewMember.extend({ role: z.literal('parent') }),
  others: z.array(NewMember).max(12).default([]),
  parentPin: z.string().regex(/^\d{4,6}$/),
  /** Start with suggested routines and rewards. */
  starterPack: z.boolean().default(true),
  /** Fill the household with the fictional demo family (for trying it out). */
  demo: z.boolean().default(false),
  /** Demo only: which parent of the demo family the caller becomes (keeps the other one). */
  demoAs: z.enum(['mama', 'papa']).default('mama'),
  /** Personal link from a test family invitation (1.17) – checked by the server. */
  invite: z.string().trim().max(100).nullish(),
});
export type CreateHouseholdInput = z.infer<typeof CreateHouseholdInput>;

/**
 * Shopping list mirrored from Bring! (households/{hid}/shopping/bring). Written only by the
 * server (sync every few minutes and after every change); every device of the family reads it.
 */
export const ShoppingItem = z.object({
  /** Bring!'s article key – needed to change the item there. */
  key: z.string(),
  /** Shown name ("Paprika"). */
  name: z.string(),
  spec: z.string().default(''),
  uuid: z.string().optional(),
});
export type ShoppingItem = z.infer<typeof ShoppingItem>;

export const ShoppingList = z.object({
  listUuid: z.string(),
  listName: z.string(),
  lists: z.array(z.object({ uuid: z.string(), name: z.string() })).default([]),
  purchase: z.array(ShoppingItem).default([]),
  recently: z.array(ShoppingItem).default([]),
  syncedAt: millis,
  /** Last sync failed: 'auth' = sign in again, otherwise Bring! is unreachable or changed. */
  error: z.enum(['auth', 'unavailable', 'changed']).nullable().default(null),
});
export type ShoppingList = z.infer<typeof ShoppingList>;

/**
 * The family's own shopping list (Release 1.20): households/{hid}/shoppingItems/{id}. Written
 * directly by every device of the family (works offline). An item is never added twice – adding
 * an article that sits under "recently bought" reopens it (aisle and history stay).
 */
export const ListItem = z.object({
  name: z.string().trim().min(1).max(80),
  spec: z.string().trim().max(80).default(''),
  aisle: Aisle,
  checked: z.boolean().default(false),
  /** When it was ticked off; null while open. Sorts "recently bought" and drives the clean-up. */
  checkedAt: millis.nullable().default(null),
  addedBy: z.string().nullable().default(null),
  createdAt: millis,
  /** How often it was bought – "Oft gekauft" and the suggestions put frequent things first. */
  count: z.number().int().nonnegative().default(0),
});
export type ListItem = z.infer<typeof ListItem>;

export const BringConnectInput = z.object({
  email: z.string().trim().email().max(200),
  password: z.string().min(1).max(200),
});
export const BringChangeInput = z.object({
  op: z.enum(['add', 'done', 'undo', 'remove']),
  name: z.string().trim().min(1).max(80),
  spec: z.string().trim().max(80).optional(),
});
export type BringChangeInput = z.infer<typeof BringChangeInput>;

/**
 * A wall pairing code is valid this long. The app counts it from the moment the code arrives,
 * never against the server's clock – a device whose clock is off would hide the code at once
 * (the nightly E2E run on 07.10. did exactly that).
 */
export const PAIRING_TTL_MS = 10 * 60_000;

/**
 * The parent PIN opens the parent mode on the wall for this long after the last touch (counted on
 * the device; every touch extends it). Mike, 24.10.: "2 Minuten nach der letzten Berührung".
 */
export const PARENT_SESSION_MS = 2 * 60_000;
